export type GovernedReceiptValueBinding = Readonly<{
  fieldPath: string;
  value: number;
  unit: string;
  scope: string;
  timeframe: string;
}>;

export type GovernedReceiptProof = Readonly<{
  receiptId: string;
  receiptHash: string;
  identity: Readonly<Record<string, string>>;
  inputDigests: Readonly<Record<string, string>>;
  freshness: Readonly<Record<string, string>>;
  computeVersions: Readonly<Record<string, string>>;
  valueBindings: readonly GovernedReceiptValueBinding[];
}>;

export type GovernedReceiptPointer = Readonly<{
  receiptId: string;
  receiptHash: string;
  fieldPath: string;
}>;

export function isGovernedReceiptProof(value: unknown): value is GovernedReceiptProof {
  return (
    isRecord(value) &&
    nonBlankFields(value, ['receiptId', 'receiptHash']) &&
    nonBlankRecord(value.identity) &&
    nonBlankRecord(value.inputDigests) &&
    nonBlankRecord(value.freshness) &&
    nonBlankRecord(value.computeVersions) &&
    Array.isArray(value.valueBindings) &&
    value.valueBindings.every(isGovernedReceiptValueBinding)
  );
}

export function isGovernedReceiptPointer(value: unknown): value is GovernedReceiptPointer {
  return isRecord(value) && nonBlankFields(value, ['receiptId', 'receiptHash', 'fieldPath']);
}

function isGovernedReceiptValueBinding(value: unknown): value is GovernedReceiptValueBinding {
  return (
    isRecord(value) &&
    typeof value.value === 'number' &&
    Number.isFinite(value.value) &&
    nonBlankFields(value, ['fieldPath', 'unit', 'scope', 'timeframe'])
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function nonBlankFields(value: Record<string, unknown>, keys: readonly string[]): boolean {
  return keys.every((key) => typeof value[key] === 'string' && value[key].trim().length > 0);
}

function nonBlankRecord(value: unknown): value is Record<string, string> {
  return (
    isRecord(value) &&
    Object.values(value).every((entry) => typeof entry === 'string' && entry.trim().length > 0)
  );
}
